import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';

import { getTokens } from 'pages/APIIntegrations/ConnectedTokens.slice';

import { SALES_CHANNELS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { PLATFORM, SalesChannelElementMapping } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import * as S from './ConnectSalesChannelForm.styled';
import { setConnectedStores } from './ConnectedStores/ConnectedStores.slice';
import SalesChannelsInstructions from './SalesChannelsInstructions';
import useSalesChannelSchema from './Stores/useSalesChannelSchema';
import useStores from './Stores/useStores';
import useSallaSync from './useSallaSync';

const ConnectSalesChannelModal = NiceModal.create(({ ID, modalName }) => {
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess, showInfo } = useAlert();
	const navigate = useNavigate();
	const { instructions } = useStores();
	const [showInstructions, setShowInstructions] = useState(false);
	const modal = useModal();
	const { salesChannel } = useSelector((state) => state.salesChannel);
	const { restartAutomation } = useSallaSync();

	const { schema, defaultValues } = useSalesChannelSchema(
		salesChannel?.formType,
		salesChannel?.slug === 'store-hippo'
	);
	const FormElement = useMemo(() => SalesChannelElementMapping[salesChannel?.formType], [salesChannel]);

	const connectedStoresList = useSelector((state) => state?.connectedStores);
	const dispatch = useDispatch();

	const isSallaAutomation = useMemo(() => {
		return (
			(salesChannel?.slug === 'salla-automation' && import.meta.env.VITE_PLATFORM === PLATFORM.desktop) ||
			salesChannel?.slug === 'salla-desktop'
		);
	}, [salesChannel]);

	const { closeModal } = useModalRouter();
	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(async () => {
		try {
			if (!ID) return defaultValues;

			const response = await axiosPrivate.post(SALES_CHANNELS.GET_SALES_CHANNEL_CREDENTIALS, {
				ID
			});
			return {
				...response.data,
				formMode: 'edit'
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			handleCloseModal();
		}
	}, []);
	const onSubmit = useCallback(async (data) => {
		try {
			const response = await axiosPrivate.post(
				ID ? SALES_CHANNELS.UPDATE_SALES_CHANNEL_CREDENTIALS : SALES_CHANNELS.ADD_SALES_CHANNEL_CREDENTIALS,
				{
					ID,
					salesChannelID: salesChannel.ID,
					...data,
					...(isSallaAutomation && {
						desktopRegistration: import.meta.env.VITE_PLATFORM === PLATFORM.desktop,
						salesChannelID: 4
					})
				}
			);

			if (response.status !== 200) {
				AppLogger.error(response);
				showError();
				return;
			}

			if (salesChannel.slug === 'oto-api') {
				formMethods.setValue('token', response.data.refreshToken);
				dispatch(getTokens());
				navigate(mergePaths(PATHS.API_INTEGRATIONS, PATHS.CONNECTED_TOKENS));
				return;
			}

			// Handle successful responses
			if (response.data.code === 'success.S001') {
				showInfo({
					header: 'info.secretKeyHere',
					message: response.data.secretToken
				});
				formMethods.setValue('token', response.data.secretToken);
			} else if (response.data.code === 'success.S002') {
				formMethods.setValue('token', response.data.refreshToken);
			} else if (response.data.sallaAppUrl) {
				window.open(response.data.sallaAppUrl, '_blank');
			} else if (response.data.authUrl) {
				window.location.href = response.data.authUrl;
			} else {
				handleCloseModal();
				showSuccess();
				if (ID) {
					dispatch(
						setConnectedStores(
							connectedStoresList.map((store) => (store.ID === ID ? { ...store, ...data } : store))
						)
					);
				}

				// Restart Salla sync automation if a Salla channel was added or updated
				if (isSallaAutomation) {
					restartAutomation();
				}
			}
		} catch (error) {
			const errorCode = error.response?.data.code;

			switch (errorCode) {
				case 'error.C015':
					showError({ message: 'error.storeNameAlreadyExists' });
					break;
				case 'error.F010':
					showError({ message: 'error.thisSalesChannelcanNotBeAddedMoreThanOnce' });
					break;
				case 'error.C018':
					showError({ message: 'error.organizationAlreadyConnectedToThisStore' });
					break;
				case 'error.F014':
					navigate(PATHS.OTO_MODULES);
					showError({
						header: 'info.limitedSalesChannelTitle',
						message: 'info.limitedSalesChannelDescription'
					});
					break;
				default:
					showError();
					AppLogger.error(error);
			}
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});
	const {
		handleSubmit,
		formState: { isSubmitting, isLoading: isFormLoading, isDirty }
	} = formMethods;

	const isLoading = isSubmitting || isFormLoading;

	return (
		<Modal position="right" fullwidth maxWidth={showInstructions ? 'medium' : 'x-small'}>
			<ModalTitle>
				<Typography variant="p1">enterStoreCredentials</Typography>
			</ModalTitle>
			<S.Content showInstructions={showInstructions}>
				<S.BlockForm formMethods={formMethods}>
					<FormElement />
				</S.BlockForm>
				{showInstructions && (
					<>
						<S.Separator />
						<S.Instructions>
							<S.InstructionsTitle>
								<Typography variant="p2" fontWeight="700">
									howToConnect
								</Typography>
							</S.InstructionsTitle>
							<S.InstructionsContent>
								<SalesChannelsInstructions instructions={instructions} />
							</S.InstructionsContent>
						</S.Instructions>
					</>
				)}
			</S.Content>

			<ModalActions>
				<Button label="cancel" variant="action" onClick={modal.remove} />

				{instructions.length > 0 && (
					<Button
						variant="outline"
						label={showInstructions ? 'hideHowToConnect' : 'showHowToConnect'}
						onClick={() => setShowInstructions(!showInstructions)}
					/>
				)}

				<Button
					label="Save"
					variant="blue"
					onClick={handleSubmit(onSubmit, AppLogger.log)}
					loading={isLoading}
					size="large"
					disabled={!isDirty || isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
				/>
			</ModalActions>
		</Modal>
	);
});

export default ConnectSalesChannelModal;
